import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
    BellIcon, 
    CheckIcon, 
    XMarkIcon,
    TrashIcon,
    EnvelopeIcon,
    UserIcon
} from '@heroicons/react/24/outline';
import { auth } from '../config/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import toast from 'react-hot-toast';

const NotificationCenter = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [processingInvitation, setProcessingInvitation] = useState(null); // Track which invitation is being processed
    const dropdownRef = useRef(null);

    // Get authenticated user ID from Firebase
    const [currentUserId, setCurrentUserId] = useState(null);

    // API base URL
    const API_BASE = 'http://localhost:5001/api';

    // Helper function to get auth headers
    const getAuthHeaders = async () => {
        const token = await auth.currentUser?.getIdToken();
        return {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        };
    };

    // Listen for Firebase auth state changes
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                // Get the user ID from our backend by sending the Firebase ID token
                try {
                    const idToken = await user.getIdToken();
                    const response = await fetch(`${API_BASE}/firebase-auth/login`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({ idToken }),
                    });
                    
                    const data = await response.json();
                    if (data.success) {
                        setCurrentUserId(data.data.user.user_id);
                        // Store the ID token for future API calls
                        localStorage.setItem('firebaseIdToken', idToken);
                    }
                } catch (error) {
                    console.error('Error getting user ID:', error);
                }
            } else {
                setCurrentUserId(null);
                localStorage.removeItem('firebaseIdToken');
            }
        });

        return () => unsubscribe();
    }, []);

    // Fetch unread notifications
    const fetchUnreadNotifications = useCallback(async () => {
        if (!currentUserId) return;
        
        try {
            const response = await fetch(`${API_BASE}/notifications/${currentUserId}/unread`, {
                headers: await getAuthHeaders()
            });
            
            if (!response.ok) {
                if (response.status === 429) {
                    console.log('Rate limited, will retry later...');
                    return;
                }
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            
            const data = await response.json();
            
            if (data.success) {
                setNotifications(data.data);
            }
        } catch (error) {
            console.error('Error fetching notifications:', error);
        }
    }, [currentUserId, API_BASE]);

    // Fetch unread count
    const fetchUnreadCount = useCallback(async () => {
        if (!currentUserId) return;
        
        try {
            const response = await fetch(`${API_BASE}/notifications/${currentUserId}/count`, {
                headers: await getAuthHeaders()
            });
            
            if (!response.ok) {
                if (response.status === 429) {
                    console.log('Rate limited, will retry later...');
                    return;
                }
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            
            const data = await response.json();
            
            if (data.success) {
                setUnreadCount(data.count);
            }
        } catch (error) {
            console.error('Error fetching notification count:', error);
        }
    }, [currentUserId, API_BASE]);

    // Mark notification as read
    const markAsRead = async (notificationId) => {
        try {
            const response = await fetch(`${API_BASE}/notifications/${notificationId}/read`, {
                method: 'POST',
                headers: await getAuthHeaders(),
                body: JSON.stringify({
                    userId: currentUserId
                }),
            });

            const data = await response.json();
            
            if (data.success) {
                // Update local state
                setNotifications(prev => 
                    prev.map(notif => 
                        notif.notification_id === notificationId 
                            ? { ...notif, is_read: true }
                            : notif
                    )
                );
                setUnreadCount(prev => Math.max(0, prev - 1));
            }
        } catch (error) {
            console.error('Error marking notification as read:', error);
        }
    };

    // Mark all notifications as read
    const markAllAsRead = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${API_BASE}/notifications/${currentUserId}/read-all`, {
                method: 'POST',
                headers: await getAuthHeaders()
            });

            const data = await response.json();
            
            if (data.success) {
                setNotifications(prev => 
                    prev.map(notif => ({ ...notif, is_read: true }))
                );
                setUnreadCount(0);
            }
        } catch (error) {
            console.error('Error marking all notifications as read:', error);
        } finally {
            setLoading(false);
        }
    };

    // Delete notification
    const deleteNotification = async (notificationId) => {
        try {
            const response = await fetch(`${API_BASE}/notifications/${notificationId}`, {
                method: 'DELETE',
                headers: await getAuthHeaders(),
                body: JSON.stringify({
                    userId: currentUserId
                }),
            });

            const data = await response.json();
            
            if (data.success) {
                // Remove from local state
                setNotifications(prev => 
                    prev.filter(notif => notif.notification_id !== notificationId)
                );
                // Update count if it was unread
                const deletedNotif = notifications.find(n => n.notification_id === notificationId);
                if (deletedNotif && !deletedNotif.is_read) {
                    setUnreadCount(prev => Math.max(0, prev - 1));
                }
            }
        } catch (error) {
            console.error('Error deleting notification:', error);
        }
    };

    // Handle group invitation acceptance/decline
    const handleGroupInvitation = async (notification, action) => {
        setProcessingInvitation(notification.notification_id);
        
        try {
            const { group_id } = notification.data;
            
            if (action === 'accept') {
                // Find the invitation ID for this group
                const invitationResponse = await fetch(`${API_BASE}/groups/invitations/${currentUserId}/${group_id}`, {
                    headers: await getAuthHeaders()
                });
                
                if (invitationResponse.ok) {
                    const invitationData = await invitationResponse.json();
                    if (invitationData.success && invitationData.data.length > 0) {
                        const invitation = invitationData.data[0];
                        
                        // Accept the invitation
                        const acceptResponse = await fetch(`${API_BASE}/groups/invitations/${invitation.invitation_id}/accept`, {
                            method: 'POST',
                            headers: await getAuthHeaders()
                        });
                        
                        if (acceptResponse.ok) {
                            // Remove the notification
                            setNotifications(prev => prev.filter(n => n.notification_id !== notification.notification_id));
                            fetchUnreadCount();
                            toast.success('🎉 Group invitation accepted! You are now a member.', {
                                duration: 4000,
                                style: {
                                    background: '#10B981',
                                    color: 'white',
                                    fontWeight: '500',
                                },
                                icon: '✅',
                            });
                        } else {
                            toast.error('Failed to accept invitation. Please try again.', {
                                duration: 4000,
                                style: {
                                    background: '#EF4444',
                                    color: 'white',
                                    fontWeight: '500',
                                },
                                icon: '❌',
                            });
                        }
                    }
                }
            } else if (action === 'decline') {
                // Find the invitation ID for this group
                const invitationResponse = await fetch(`${API_BASE}/groups/invitations/${currentUserId}/${group_id}`, {
                    headers: await getAuthHeaders()
                });
                
                if (invitationResponse.ok) {
                    const invitationData = await invitationResponse.json();
                    if (invitationData.success && invitationData.data.length > 0) {
                        const invitation = invitationData.data[0];
                        
                        // Decline the invitation
                        const declineResponse = await fetch(`${API_BASE}/groups/invitations/${invitation.invitation_id}/decline`, {
                            method: 'POST',
                            headers: await getAuthHeaders()
                        });
                        
                        if (declineResponse.ok) {
                            // Remove the notification
                            setNotifications(prev => prev.filter(n => n.notification_id !== notification.notification_id));
                            fetchUnreadCount();
                            toast.success('Group invitation declined', {
                                duration: 3000,
                                style: {
                                    background: '#6B7280',
                                    color: 'white',
                                    fontWeight: '500',
                                },
                                icon: '👋',
                            });
                        } else {
                            toast.error('Failed to decline invitation. Please try again.', {
                                duration: 4000,
                                style: {
                                    background: '#EF4444',
                                    color: 'white',
                                    fontWeight: '500',
                                },
                                icon: '❌',
                            });
                        }
                    }
                }
            }
        } catch (error) {
            console.error('Error handling group invitation:', error);
            toast.error('Something went wrong. Please try again.', {
                duration: 4000,
                style: {
                    background: '#EF4444',
                    color: 'white',
                    fontWeight: '500',
                },
                icon: '⚠️',
            });
        } finally {
            setProcessingInvitation(null);
        }
    };

    // Format timestamp
    const formatTimestamp = (timestamp) => {
        const date = new Date(timestamp);
        const now = new Date();
        const diffInMinutes = Math.floor((now - date) / (1000 * 60));
        
        if (diffInMinutes < 1) return 'Just now';
        if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
        if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
        return date.toLocaleDateString();
    };

    // Get notification icon based on type
    const getNotificationIcon = (type) => {
        switch (type) {
            case 'friend_request':
                return <UserIcon className="h-5 w-5 text-blue-600" />;
            case 'friend_accepted':
                return <CheckIcon className="h-5 w-5 text-green-600" />;
            case 'friend_declined':
                return <XMarkIcon className="h-5 w-5 text-red-600" />;
            case 'friend_removed':
                return <TrashIcon className="h-5 w-5 text-red-600" />;
            default:
                return <EnvelopeIcon className="h-5 w-5 text-gray-600" />;
        }
    };

    // Get notification color based on type
    const getNotificationColor = (type) => {
        switch (type) {
            case 'friend_request':
                return 'bg-blue-50 border-blue-200';
            case 'friend_accepted':
                return 'bg-green-50 border-green-200';
            case 'friend_declined':
                return 'bg-red-50 border-red-200';
            case 'friend_removed':
                return 'bg-red-50 border-red-200';
            default:
                return 'bg-gray-50 border-gray-200';
        }
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Fetch data when user ID is available
    useEffect(() => {
        if (currentUserId) {
            fetchUnreadNotifications();
            fetchUnreadCount();
        }
    }, [currentUserId, fetchUnreadNotifications, fetchUnreadCount]);

    // Refresh data every 2 minutes when user ID is available (reduced from 30 seconds)
    useEffect(() => {
        if (!currentUserId) return;
        
        const interval = setInterval(() => {
            fetchUnreadNotifications();
            fetchUnreadCount();
        }, 120000); // 2 minutes

        return () => clearInterval(interval);
    }, [currentUserId, fetchUnreadNotifications, fetchUnreadCount]);

    return (
        <div className="relative" ref={dropdownRef}>
            {/* Notification Bell */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors duration-200"
                title="Notifications"
            >
                <BellIcon className="h-6 w-6" />
                {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-medium">
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                )}
            </button>

            {/* Dropdown */}
            {isOpen && (
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                    {/* Header */}
                    <div className="px-4 py-3 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                            <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
                            {unreadCount > 0 && (
                                <button
                                    onClick={markAllAsRead}
                                    disabled={loading}
                                    className="text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50"
                                >
                                    {loading ? 'Marking...' : 'Mark all read'}
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Notifications List */}
                    <div className="max-h-96 overflow-y-auto">
                        {notifications.length === 0 ? (
                            <div className="px-4 py-8 text-center">
                                <EnvelopeIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                                <p className="text-gray-600">No notifications</p>
                                <p className="text-sm text-gray-500">You're all caught up!</p>
                            </div>
                        ) : (
                            <div className="divide-y divide-gray-200">
                                {notifications.map((notification) => (
                                    <div
                                        key={notification.notification_id}
                                        className={`p-4 hover:bg-gray-50 transition-colors duration-200 ${getNotificationColor(notification.type)}`}
                                    >
                                        <div className="flex items-start space-x-3">
                                            <div className="flex-shrink-0 mt-1">
                                                {getNotificationIcon(notification.type)}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-gray-900">
                                                    {notification.title}
                                                </p>
                                                <p className="text-sm text-gray-600 mt-1">
                                                    {notification.body}
                                                </p>
                                                <p className="text-xs text-gray-500 mt-2">
                                                    {formatTimestamp(notification.created_at)}
                                                </p>
                                            </div>
                                            <div className="flex-shrink-0 flex space-x-1">
                                                {!notification.is_read && (
                                                    <button
                                                        onClick={() => markAsRead(notification.notification_id)}
                                                        className="text-blue-600 hover:text-blue-800 p-1 rounded"
                                                        title="Mark as read"
                                                    >
                                                        <CheckIcon className="h-4 w-4" />
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => deleteNotification(notification.notification_id)}
                                                    className="text-gray-400 hover:text-red-600 p-1 rounded"
                                                    title="Delete notification"
                                                >
                                                    <TrashIcon className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </div>
                                        {/* Action buttons for group invitations */}
                                        {notification.type === 'group_invitation' && notification.data?.group_id && (
                                            <div className="mt-3 flex space-x-2">
                                                <button
                                                    onClick={() => handleGroupInvitation(notification, 'accept')}
                                                    disabled={processingInvitation === notification.notification_id}
                                                    className={`px-3 py-1 text-white text-xs rounded transition-colors flex items-center space-x-1 ${
                                                        processingInvitation === notification.notification_id
                                                            ? 'bg-green-500 cursor-not-allowed'
                                                            : 'bg-green-600 hover:bg-green-700'
                                                    }`}
                                                >
                                                    {processingInvitation === notification.notification_id ? (
                                                        <>
                                                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                                                            <span>Accepting...</span>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <span>✅</span>
                                                            <span>Accept</span>
                                                        </>
                                                    )}
                                                </button>
                                                <button
                                                    onClick={() => handleGroupInvitation(notification, 'decline')}
                                                    disabled={processingInvitation === notification.notification_id}
                                                    className={`px-3 py-1 text-white text-xs rounded transition-colors flex items-center space-x-1 ${
                                                        processingInvitation === notification.notification_id
                                                            ? 'bg-red-500 cursor-not-allowed'
                                                            : 'bg-red-600 hover:bg-red-700'
                                                    }`}
                                                >
                                                    {processingInvitation === notification.notification_id ? (
                                                        <>
                                                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                                                            <span>Processing...</span>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <span>❌</span>
                                                            <span>Decline</span>
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    {notifications.length > 0 && (
                        <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 rounded-b-lg">
                            <button
                                onClick={() => window.location.href = '/notifications'}
                                className="w-full text-sm text-blue-600 hover:text-blue-800 font-medium"
                            >
                                View all notifications
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default NotificationCenter;
